<script setup>
import { reactive, computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import WhatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';
import TemplateSchedulesAPI from 'dashboard/api/templateSchedules';
import AudioRecorderManager from './AudioRecorderManager.vue';

const store = useStore();
const whatsappInboxes = useMapGetter('inboxes/getWhatsAppInboxes');

const LANGUAGES = [
  { value: 'pt_BR', label: 'Português (Brasil)' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'es_ES', label: 'Español (España)' },
  { value: 'es_AR', label: 'Español (Argentina)' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
  { value: 'nl', label: 'Nederlands' },
];

const CATEGORIES = [
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'UTILITY', label: 'Utility' },
  { value: 'AUTHENTICATION', label: 'Authentication' },
];

const state = reactive({
  inboxId: '',
  templateName: '',
  language: 'pt_BR',
  category: 'MARKETING',
  bodyText: '',
  intervalMinutes: 5,
  totalCount: 10,
});

const bodyExamples = ref({});
const isCreating = ref(false);

// Scheduler state
const activeSchedule = ref(null);
const scheduleHistory = ref([]);
const selectedSchedule = ref(null);
const isStarting = ref(false);
const pollingTimer = ref(null);

const inboxOptions = computed(() =>
  whatsappInboxes.value.map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const bodyVariables = computed(() => {
  const matches = [...state.bodyText.matchAll(/\{\{(\d+)\}\}/g)];
  return [...new Set(matches.map(m => m[1]))].sort((a, b) => Number(a) - Number(b));
});

watch(bodyVariables, vars => {
  vars.forEach(v => { if (!(v in bodyExamples.value)) bodyExamples.value[v] = ''; });
  Object.keys(bodyExamples.value).forEach(k => { if (!vars.includes(k)) delete bodyExamples.value[k]; });
});

const allExamplesFilled = computed(() =>
  bodyVariables.value.every(v => bodyExamples.value[v]?.trim())
);

const isFormValid = computed(() =>
  state.inboxId &&
  state.templateName.trim() &&
  state.language &&
  state.category &&
  state.bodyText.trim() &&
  (bodyVariables.value.length === 0 || allExamplesFilled.value)
);

const sanitizedName = computed(() =>
  state.templateName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
);

// ETA
const etaMinutes = computed(() =>
  state.totalCount <= 1 ? 0 : (state.totalCount - 1) * state.intervalMinutes
);

const etaText = computed(() => {
  const mins = etaMinutes.value;
  if (mins === 0) return 'imediato';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
});

const isSchedulerRunning = computed(() => activeSchedule.value?.status === 'running');

const scheduleProgress = computed(() => {
  if (!activeSchedule.value) return 0;
  const { completed_count, total_count } = activeSchedule.value;
  return total_count > 0 ? Math.round((completed_count / total_count) * 100) : 0;
});

const remainingEta = computed(() => {
  if (!activeSchedule.value) return '';
  const remaining = activeSchedule.value.total_count - activeSchedule.value.completed_count;
  if (remaining <= 0) return 'Concluído';
  const mins = (remaining - 1) * activeSchedule.value.interval_minutes;
  if (mins === 0) return 'Último em execução...';
  if (mins < 60) return `~${mins} min restantes`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `~${h > 0 ? `${h}h ` : ''}${m > 0 ? `${m}min ` : ''}restantes`;
});

const displayedSchedule = computed(() => selectedSchedule.value || activeSchedule.value);

// Single create
const buildComponents = () => {
  const body = { type: 'BODY', text: state.bodyText };
  if (bodyVariables.value.length > 0) {
    body.example = { body_text: [bodyVariables.value.map(v => bodyExamples.value[v] || `exemplo_${v}`)] };
  }
  return [body];
};

const createTemplate = async () => {
  if (!isFormValid.value) return;
  isCreating.value = true;
  try {
    await WhatsappTemplatesAPI.create(state.inboxId, {
      name: sanitizedName.value,
      language: state.language,
      category: state.category,
      components: buildComponents(),
    });
  } finally {
    isCreating.value = false;
  }
};

// Scheduler
const startScheduler = async () => {
  if (!isFormValid.value || isStarting.value) return;
  isStarting.value = true;
  try {
    const res = await TemplateSchedulesAPI.create({
      inbox_id: state.inboxId,
      template_name: sanitizedName.value,
      language: state.language,
      category: state.category,
      body_text: state.bodyText,
      body_examples: { ...bodyExamples.value },
      total_count: state.totalCount,
      interval_minutes: state.intervalMinutes,
    });
    activeSchedule.value = res.data;
    selectedSchedule.value = null;
    scheduleHistory.value.unshift(res.data);
    startPolling();
  } catch (err) {
    // error visible in browser console
  } finally {
    isStarting.value = false;
  }
};

const stopScheduler = async () => {
  if (!activeSchedule.value) return;
  await TemplateSchedulesAPI.stop(activeSchedule.value.id);
  activeSchedule.value = { ...activeSchedule.value, status: 'stopped' };
  stopPolling();
  refreshHistory();
};

const killSchedule = async (s) => {
  await TemplateSchedulesAPI.kill(s.id);
  if (activeSchedule.value?.id === s.id) {
    activeSchedule.value = { ...activeSchedule.value, status: 'stopped' };
    stopPolling();
  }
  if (selectedSchedule.value?.id === s.id) {
    selectedSchedule.value = { ...selectedSchedule.value, status: 'stopped' };
  }
  await refreshHistory();
};

const startPolling = () => {
  stopPolling();
  pollingTimer.value = setInterval(pollStatus, 3000);
};

const stopPolling = () => {
  if (pollingTimer.value) { clearInterval(pollingTimer.value); pollingTimer.value = null; }
};

const pollStatus = async () => {
  if (!activeSchedule.value) return;
  try {
    const res = await TemplateSchedulesAPI.get(activeSchedule.value.id);
    activeSchedule.value = res.data;
    if (!isSchedulerRunning.value) {
      stopPolling();
      refreshHistory();
    }
  } catch { stopPolling(); }
};

const refreshHistory = async () => {
  const res = await TemplateSchedulesAPI.list();
  scheduleHistory.value = res.data;
};

const selectSchedule = async (s) => {
  if (selectedSchedule.value?.id === s.id) { selectedSchedule.value = null; return; }
  const res = await TemplateSchedulesAPI.get(s.id);
  selectedSchedule.value = res.data;
};

const statusLabel = s => ({ running: 'Executando', completed: 'Concluído', stopped: 'Parado', pending: 'Pendente' }[s] || s);
const statusClass = s => ({
  running: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  stopped: 'bg-n-alpha-2 text-n-slate-10',
  pending: 'bg-yellow-100 text-yellow-700',
}[s] || '');

const execStatusClass = s => ({
  success: 'bg-green-50 text-green-700',
  error: 'bg-red-50 text-red-700',
  pending: 'bg-yellow-50 text-yellow-700',
}[s] || '');

const formatTime = iso => new Date(iso).toLocaleTimeString();
const formatDateTime = iso => new Date(iso).toLocaleString();

onMounted(async () => {
  store.dispatch('inboxes/get');
  const res = await TemplateSchedulesAPI.list();
  scheduleHistory.value = res.data;
  const running = scheduleHistory.value.find(s => s.status === 'running');
  if (running) {
    const full = await TemplateSchedulesAPI.get(running.id);
    activeSchedule.value = full.data;
    startPolling();
  } else if (scheduleHistory.value.length > 0) {
    const full = await TemplateSchedulesAPI.get(scheduleHistory.value[0].id);
    activeSchedule.value = full.data;
  }
});

onUnmounted(stopPolling);
</script>

<template>
  <div class="flex flex-col flex-1 h-full overflow-auto bg-n-surface-1">
    <div class="px-8 py-6 border-b border-n-weak">
      <h1 class="text-xl font-semibold text-n-slate-12">Criar Template WhatsApp</h1>
      <p class="mt-1 text-sm text-n-slate-11">
        Crie templates diretamente na WhatsApp Business API. Use o Scheduler para criar em lote com jobs em background.
      </p>
    </div>

    <div class="flex gap-6 px-8 py-6">
      <!-- Left column -->
      <div class="flex-1 flex flex-col gap-5 min-w-0">

        <!-- Template Config -->
        <div class="p-5 rounded-xl border border-n-weak bg-n-surface-2">
          <h2 class="text-base font-semibold text-n-slate-12 mb-4">Configuração do Template</h2>

          <div class="flex flex-col gap-1 mb-4">
            <label class="text-sm font-medium text-n-slate-12">Inbox WhatsApp <span class="text-red-500">*</span></label>
            <select v-model="state.inboxId" class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none">
              <option value="" disabled>Selecione um inbox</option>
              <option v-for="opt in inboxOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </div>

          <div class="flex flex-col gap-1 mb-4">
            <label class="text-sm font-medium text-n-slate-12">Nome do Template <span class="text-red-500">*</span></label>
            <input v-model="state.templateName" type="text" placeholder="ex: boas_vindas_cliente"
              class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none" />
            <p v-if="state.templateName" class="text-xs text-n-slate-10 mt-0.5">
              Nome base: <strong class="text-n-slate-11">{{ sanitizedName }}</strong>
              <span class="text-n-slate-10"> (scheduler usa {{ sanitizedName }}_001, _002...)</span>
            </p>
          </div>

          <div class="flex gap-4 mb-4">
            <div class="flex flex-col gap-1 flex-1">
              <label class="text-sm font-medium text-n-slate-12">Idioma <span class="text-red-500">*</span></label>
              <select v-model="state.language" class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none">
                <option v-for="lang in LANGUAGES" :key="lang.value" :value="lang.value">{{ lang.label }}</option>
              </select>
            </div>
            <div class="flex flex-col gap-1 flex-1">
              <label class="text-sm font-medium text-n-slate-12">Categoria <span class="text-red-500">*</span></label>
              <select v-model="state.category" class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none">
                <option v-for="cat in CATEGORIES" :key="cat.value" :value="cat.value">{{ cat.label }}</option>
              </select>
            </div>
          </div>

          <div class="flex flex-col gap-1 mb-4">
            <label class="text-sm font-medium text-n-slate-12">Corpo do Template <span class="text-red-500">*</span></label>
            <textarea v-model="state.bodyText" rows="4"
              placeholder="Olá {{1}}, temos uma novidade para você!"
              class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 resize-none focus:outline-none" />
            <p class="text-xs text-n-slate-10 mt-0.5">Use &#123;&#123;1&#125;&#125;, &#123;&#123;2&#125;&#125;... para variáveis dinâmicas.</p>
          </div>

          <div v-if="bodyVariables.length > 0" class="mb-4 p-4 rounded-lg bg-yellow-50 border border-yellow-200">
            <p class="text-xs font-semibold text-yellow-800 mb-3">Valores de exemplo para as variáveis:</p>
            <div v-for="varIdx in bodyVariables" :key="varIdx" class="flex items-center gap-3 mb-2">
              <span class="text-xs font-mono text-yellow-700 w-10 shrink-0">&#123;&#123;{{ varIdx }}&#125;&#125;</span>
              <input v-model="bodyExamples[varIdx]" type="text" :placeholder="`Exemplo para variável ${varIdx}`"
                class="flex-1 rounded border border-yellow-300 bg-white px-2 py-1 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-yellow-400" />
            </div>
          </div>

          <button :disabled="!isFormValid || isCreating || isSchedulerRunning"
            class="w-full rounded-lg bg-woot-500 py-2.5 text-sm font-semibold text-white hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed"
            @click="createTemplate">
            <span v-if="isCreating">Criando...</span>
            <span v-else>Criar Template Agora (único)</span>
          </button>
        </div>

        <!-- Scheduler -->
        <div class="p-5 rounded-xl border border-n-weak bg-n-surface-2">
          <h2 class="text-base font-semibold text-n-slate-12 mb-4">Scheduler em Background</h2>

          <div class="flex gap-4 mb-3">
            <div class="flex flex-col gap-1 flex-1">
              <label class="text-sm font-medium text-n-slate-12">Quantidade</label>
              <input v-model.number="state.totalCount" type="number" min="1" max="1000"
                :disabled="isSchedulerRunning"
                class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none disabled:opacity-50" />
            </div>
            <div class="flex flex-col gap-1 flex-1">
              <label class="text-sm font-medium text-n-slate-12">Intervalo (minutos)</label>
              <input v-model.number="state.intervalMinutes" type="number" min="1" max="1440"
                :disabled="isSchedulerRunning"
                class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none disabled:opacity-50" />
            </div>
          </div>

          <p class="text-xs text-n-slate-10 mb-4">
            Duração total estimada: <strong class="text-n-slate-12">{{ etaText }}</strong>
            <span class="ml-2 text-n-slate-10">({{ state.totalCount }} templates × {{ state.intervalMinutes }} min)</span>
          </p>

          <div class="flex gap-3 mb-4">
            <button
              :disabled="!isFormValid || isStarting || isSchedulerRunning"
              class="flex-1 rounded-lg bg-green-600 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              @click="startScheduler">
              {{ isStarting ? 'Iniciando...' : 'Iniciar Scheduler' }}
            </button>
            <button v-if="isSchedulerRunning"
              class="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700"
              @click="stopScheduler">
              Parar
            </button>
          </div>

          <!-- Active schedule progress -->
          <div v-if="activeSchedule" class="rounded-lg border border-n-weak bg-n-surface-1 p-4">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <span class="inline-block w-2 h-2 rounded-full"
                  :class="isSchedulerRunning ? 'bg-blue-500 animate-pulse' : activeSchedule.status === 'completed' ? 'bg-green-500' : 'bg-n-slate-9'" />
                <span class="text-sm font-medium text-n-slate-12">{{ statusLabel(activeSchedule.status) }}</span>
              </div>
              <span class="text-xs text-n-slate-10">{{ remainingEta }}</span>
            </div>
            <div class="flex items-center gap-3">
              <div class="flex-1 h-2 rounded-full bg-n-alpha-3 overflow-hidden">
                <div class="h-full rounded-full bg-woot-500 transition-all duration-500"
                  :style="{ width: `${scheduleProgress}%` }" />
              </div>
              <span class="text-xs font-mono text-n-slate-11 shrink-0">
                {{ activeSchedule.completed_count }}/{{ activeSchedule.total_count }}
              </span>
            </div>
          </div>
        </div>

        <!-- Audio Recorder -->
        <AudioRecorderManager />

      </div>

      <!-- Right column: log + history -->
      <div class="w-96 flex flex-col gap-5">

        <!-- Execution log for active/selected schedule -->
        <div class="p-5 rounded-xl border border-n-weak bg-n-surface-2 flex flex-col" style="max-height: 32rem;">
          <div class="flex items-center justify-between mb-3 shrink-0">
            <h2 class="text-base font-semibold text-n-slate-12">
              Log de Execução
              <span v-if="displayedSchedule" class="text-xs font-normal text-n-slate-10 ml-1">
                #{{ displayedSchedule.id }}
              </span>
            </h2>
            <span v-if="isSchedulerRunning"
              class="flex items-center gap-1.5 text-xs text-blue-600 font-medium">
              <span class="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
              ao vivo
            </span>
          </div>

          <div v-if="!displayedSchedule" class="text-sm text-n-slate-10 text-center py-8">
            Nenhum scheduler iniciado ainda.
          </div>
          <div v-else-if="!displayedSchedule.template_executions?.length" class="text-sm text-n-slate-10 text-center py-8">
            Aguardando primeira execução...
          </div>
          <div v-else class="flex flex-col gap-1.5 overflow-y-auto">
            <div v-for="ex in displayedSchedule.template_executions" :key="ex.id"
              class="flex flex-col gap-0.5 rounded-lg px-3 py-2 text-xs"
              :class="execStatusClass(ex.status)">
              <div class="flex items-center justify-between">
                <span class="font-semibold uppercase tracking-wide opacity-70" style="font-size:9px">
                  {{ ex.status === 'success' ? '✓ sucesso' : ex.status === 'error' ? '✗ erro' : '⏳ pendente' }}
                </span>
                <span class="font-mono opacity-60" style="font-size:9px">{{ formatTime(ex.created_at) }}</span>
              </div>
              <span class="leading-snug">{{ ex.message }}</span>
            </div>
          </div>
        </div>

        <!-- Schedule history -->
        <div class="p-5 rounded-xl border border-n-weak bg-n-surface-2">
          <div class="flex items-center justify-between mb-3">
            <h2 class="text-base font-semibold text-n-slate-12">Histórico</h2>
            <button class="text-xs text-n-slate-10 hover:text-n-slate-12" @click="refreshHistory">Atualizar</button>
          </div>
          <div v-if="!scheduleHistory.length" class="text-sm text-n-slate-10 text-center py-4">Nenhum histórico.</div>
          <div v-else class="flex flex-col gap-1.5">
            <div
              v-for="s in scheduleHistory" :key="s.id"
              class="rounded-lg border transition"
              :class="selectedSchedule?.id === s.id
                ? 'border-woot-500 bg-woot-50'
                : 'border-n-weak bg-n-surface-1'">
              <!-- Row: click to select -->
              <button
                class="flex items-center justify-between w-full px-3 py-2 text-left"
                @click="selectSchedule(s)">
                <div class="min-w-0">
                  <p class="text-xs font-mono text-n-slate-12 truncate">{{ s.template_name }}</p>
                  <p class="text-xs text-n-slate-10">{{ formatDateTime(s.created_at) }}</p>
                </div>
                <div class="flex flex-col items-end gap-1 shrink-0 ml-2">
                  <span class="rounded-full px-2 py-0.5 text-xs font-medium" :class="statusClass(s.status)">
                    {{ statusLabel(s.status) }}
                  </span>
                  <span class="text-xs text-n-slate-10">{{ s.completed_count }}/{{ s.total_count }}</span>
                </div>
              </button>
              <!-- Kill button — shown for running OR stuck schedules -->
              <div v-if="s.status === 'running'" class="px-3 pb-2">
                <button
                  class="w-full rounded bg-red-100 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-200"
                  @click.stop="killSchedule(s)">
                  ⛔ Forçar Kill — remover da fila
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>
